import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { collectSalesPeople } from "@/hooks/use-sales-dashboard-data";
import { useSalesUnits } from "@/hooks/use-sales-units";
import type { PipeFilter, PipeSourceFilter } from "@/lib/pipe-filter";

const ALL = "__all__";
const SOURCE_OPTIONS: { value: PipeSourceFilter; label: string }[] = [
  { value: "all", label: "Alle kilder" },
  { value: "manual", label: "Manuell" },
  { value: "signal", label: "Signal" },
  { value: "lead", label: "Lead" },
];

interface PipeFilterBarProps {
  filter: PipeFilter;
  onChange: (patch: Partial<PipeFilter>) => void;
  showClosed: boolean;
  onToggleClosed: () => void;
}

/** Mine/Alle, rådgiver, kilde og «Vis avsluttede». */
export default function PipeFilterBar({ filter, onChange, showClosed, onToggleClosed }: PipeFilterBarProps) {
  const { data: units = [] } = useSalesUnits();
  const people = useMemo(() => collectSalesPeople(units), [units]);
  const isSpecificPerson = filter.person !== "mine" && filter.person !== "all";

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label="Hvem sin pipe" className="flex">
        <Button
          type="button"
          size="sm"
          variant={filter.person === "mine" ? "default" : "outline"}
          aria-pressed={filter.person === "mine"}
          onClick={() => onChange({ person: "mine" })}
        >
          Mine
        </Button>
        <Button
          type="button"
          size="sm"
          variant={filter.person === "all" ? "default" : "outline"}
          aria-pressed={filter.person === "all"}
          onClick={() => onChange({ person: "all" })}
        >
          Alle
        </Button>
      </div>

      <Select
        value={isSpecificPerson ? filter.person : ALL}
        onValueChange={(v) => onChange({ person: v === ALL ? "all" : v })}
      >
        <SelectTrigger className="w-48" aria-label="Velg rådgiver">
          <SelectValue placeholder="Alle rådgivere" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Alle rådgivere</SelectItem>
          {people.map((p) => (
            <SelectItem key={p.userId} value={p.userId}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filter.source} onValueChange={(v) => onChange({ source: v as PipeSourceFilter })}>
        <SelectTrigger className="w-40" aria-label="Velg kilde">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOURCE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button type="button" size="sm" variant={showClosed ? "default" : "outline"} aria-pressed={showClosed} onClick={onToggleClosed}>
        Vis avsluttede
      </Button>
    </div>
  );
}
